import { useQuery } from "@tanstack/react-query"
import { CircleAlert, FileDown, Lock } from "lucide-react"
import { useRef, useState } from "react"
import { useParams } from "react-router"

import { api } from "@/api/cliente"
import { LogoSetieca } from "@/componentes/marca/LogoSetieca"
import { descargar, logoComoPng } from "@/componentes/reportes/pdfReporte"
import { pdfDeLaSemana } from "@/componentes/reportes/pdfSemana"
import type { Saldo } from "@/componentes/reportes/tipos"
import { nombreDelPago, type Semana } from "@/componentes/reportes/tiposSemana"
import { monto } from "@/componentes/tesoreria/dinero"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

/**
 * Los pagos programados de una semana, desde el enlace del aviso de los lunes
 * (`/s/<token>`, 13/10). **Sin sesión, sin menú y sin poder tocar nada**, como el
 * reporte de cuentas (`ReportePublico`), del que copia el aspecto: membrete, resumen
 * arriba y una tabla por bloque, que en el teléfono se apila en tarjetas.
 *
 * ⚠ **Es la FOTO del lunes**, no una consulta en vivo: dice lo que decía el mensaje. Lo
 * que se pagó durante la semana se ve en la bandeja de órdenes, no aquí.
 *
 * ⚠ **Un día sin pagos se dice**, no se esconde: «nada el miércoles» es tan dato como lo
 * que vence el jueves, y una semana con huecos parece incompleta.
 *
 * ⚠ **«Por generar» va en rojo**: es lo que nadie ha encaminado todavía, y es lo que se
 * viene a mirar el lunes.
 */

function saldos(lista: Saldo[]): string {
  return lista.length === 0 ? "—" : lista.map((s) => `${s.moneda} ${monto(s.saldo)}`).join(" + ")
}

export function SemanaPublica() {
  const { token = "" } = useParams()
  const marca = useRef<HTMLDivElement>(null)
  const [generando, setGenerando] = useState(false)
  const [fallo, setFallo] = useState(false)
  const consulta = useQuery({
    queryKey: ["publico", "semana", token],
    queryFn: () => api.get<Semana>(`/api/v1/publico/semana/${token}`),
    retry: false,
  })

  if (consulta.isPending) {
    return (
      <p role="status" className="p-10 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }
  if (consulta.isError || !consulta.data) {
    return (
      <div className="mx-auto max-w-lg p-10 text-center">
        <CircleAlert size={28} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
        <h1 className="mt-3 text-lg font-semibold">Este enlace ya no vale</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Puede haber caducado. El del lunes trae la semana nueva.
        </p>
      </div>
    )
  }

  const s = consulta.data
  const tenue = { color: "var(--fg-muted)" }
  const borde = { borderColor: "var(--border)" }
  const rojo = { color: "var(--destructive)" }

  async function exportar() {
    setGenerando(true)
    setFallo(false)
    try {
      const logo = await logoComoPng(
        marca.current?.querySelector("svg") ?? null,
        "rgb(122, 27, 56)",
      )
      descargar(
        await pdfDeLaSemana(s, logo),
        `Pagos de la semana ${comoFecha(s.lunes).replaceAll("/", "-")}.pdf`,
      )
    } catch {
      setFallo(true)
    } finally {
      setGenerando(false)
    }
  }

  return (
    <div className="hoja-reporte mx-auto max-w-6xl p-4 sm:p-8">
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-3">
        <p
          className="flex items-center gap-2 rounded-[var(--radius-control)] border border-dashed px-3 py-2 text-xs"
          style={{ ...borde, ...tenue }}
        >
          <Lock size={12} aria-hidden={true} />
          Vista de sólo lectura. El enlace caduca el {comoFecha(s.expira_en)}.
        </p>
        <button
          type="button"
          onClick={exportar}
          disabled={generando}
          className="foco-cristal inline-flex min-h-11 items-center gap-2 rounded-[var(--radius-control)] border px-4 text-sm font-medium disabled:opacity-60"
          style={borde}
        >
          <FileDown size={16} aria-hidden={true} />
          {generando ? "Generando…" : "Exportar PDF"}
        </button>
        {fallo && (
          <p role="alert" className="w-full text-xs" style={rojo}>
            No se pudo generar el PDF. Vuelve a intentarlo.
          </p>
        )}
      </div>

      <header className="mb-5 flex items-center justify-between gap-4 border-b pb-3" style={borde}>
        <div ref={marca} className="shrink-0" style={{ color: "var(--fg)" }}>
          <LogoSetieca titulo={s.empresa_nombre} className="h-12 w-auto sm:h-14" />
        </div>
        <div className="min-w-0 text-right">
          <p className="text-sm font-semibold uppercase sm:text-base">{s.empresa_nombre}</p>
          {s.empresa_rif ? (
            <p className="text-xs" style={tenue}>
              RIF {s.empresa_rif}
            </p>
          ) : null}
        </div>
      </header>

      <p className="text-xs" style={tenue}>
        Generado el {comoFechaHora(s.generado_en)}
      </p>
      <h1 className="mt-1 text-xl font-semibold">{s.titulo}</h1>

      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-[var(--radius-control)] border p-3" style={borde}>
          <dt style={tenue}>Pagos de la semana</dt>
          <dd className="mt-1 text-base font-semibold tabular-nums">
            {s.total_pagos} · {saldos(s.total)}
          </dd>
        </div>
        <div className="rounded-[var(--radius-control)] border p-3" style={borde}>
          <dt style={tenue}>Gastos programados</dt>
          <dd className="mt-1 text-base font-semibold tabular-nums">{saldos(s.gastos)}</dd>
        </div>
        <div className="rounded-[var(--radius-control)] border p-3" style={borde}>
          <dt style={tenue}>Nómina</dt>
          <dd className="mt-1 text-base font-semibold tabular-nums">{saldos(s.nomina)}</dd>
        </div>
        <div className="rounded-[var(--radius-control)] border p-3" style={borde}>
          <dt style={tenue}>Por generar</dt>
          <dd
            className="mt-1 text-base font-semibold tabular-nums"
            style={s.por_generar > 0 ? rojo : undefined}
          >
            {s.por_generar}
            {s.por_generar > 0 ? ` · ${saldos(s.saldos_por_generar)}` : ""}
          </dd>
        </div>
      </dl>

      {s.dias.map((dia) => (
        <section key={dia.fecha} className="grupo-reporte mt-8">
          <div
            className="flex flex-wrap items-baseline justify-between gap-2 border-b pb-1"
            style={borde}
          >
            <h2 className="text-base font-semibold">
              {dia.nombre} <span className="tabular-nums">{comoFecha(dia.fecha)}</span>
            </h2>
            <p className="text-sm font-semibold tabular-nums">
              {dia.pagos.length > 0 ? saldos(dia.total) : ""}
            </p>
          </div>
          {dia.pagos.length === 0 ? (
            <p className="mt-2 text-sm" style={tenue}>
              Sin pagos programados.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="tabla-apilable mt-2 w-full text-sm">
                <thead>
                  <tr className="text-left text-xs" style={tenue}>
                    <th className="py-1 pr-3 font-medium">Pago</th>
                    <th className="py-1 pr-3 font-medium">A quién</th>
                    <th className="py-1 pr-3 font-medium">Situación</th>
                    <th className="py-1 text-right font-medium">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {dia.pagos.map((p) => (
                    <tr
                      // Una foto que no cambia: en un mismo día, un pago no se repite con el
                      // mismo concepto, destinatario, monto y situación.
                      key={`${p.origen}-${p.concepto}-${p.tercero}-${p.monto}-${p.situacion}`}
                      className="border-t align-top"
                      style={borde}
                    >
                      <td data-rotulo="Pago" className="py-1 pr-3">
                        {nombreDelPago(p)}
                      </td>
                      <td data-rotulo="A quién" className="py-1 pr-3">
                        {p.tercero ?? "—"}
                      </td>
                      <td
                        data-rotulo="Situación"
                        className="py-1 pr-3"
                        style={p.encaminado ? tenue : { ...rojo, fontWeight: 600 }}
                      >
                        {p.situacion}
                      </td>
                      <td
                        data-rotulo="Monto"
                        className="whitespace-nowrap py-1 text-right font-medium tabular-nums"
                      >
                        {p.divisa} {monto(p.monto)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}
    </div>
  )
}
